import { ScimReference, Reference } from './ReferenceTypes';

// Lightweight locality types for efficient table display
// Reduces server load by only requesting essential attributes

export interface BaseLocality {
    DisplayName: string; // from SCIM DisplayName
    Type: string;        // from SCIM EntityType/Type
    Id: string;          // from SCIM Id
    Address?: string;    // Address is a key determinant of locality entities
    Actions?: string[];  // Future: string array (not yet in API)
    rawData?: any;       // Raw JSON data from the API for debug views
}

export interface Building extends BaseLocality {
    // Building-specific fields if needed
}

export interface School extends BaseLocality {
    // School-specific fields if needed
}

// Full types for detail views and mock data (includes all properties)
export interface FullSchool {
    Id: string;
    DisplayName: string;
    EntityType: string | Reference; // Can be string or reference object
    Address?: string;
    Building?: ScimReference;
    City?: string;
    GradeLevels?: string[];
    PhoneNumber?: string;
    Principals?: ScimReference[];
    Rc?: ScimReference;
    Region?: number;
    RegionLabel?: string;
    Role?: string;
    State?: string;
    Type?: string;
    Zip?: string;
    ObjectId?: string;
    /** Set by detail view when geocoding an address (SchoolMap uses these when present). */
    Latitude?: number;
    Longitude?: number;
}

export interface FullBuilding {
    Id: string;
    DisplayName: string;
    EntityType: string | Reference; // Can be string or reference object
    Address?: string;
    City?: string;
    State?: string;
    Zip?: string;
    Manager?: ScimReference;
    Assistant?: ScimReference;
    BackupManager?: ScimReference;
    BackupAssistant?: ScimReference;
    District?: boolean;
    Type?: string;
    OpenTime?: string;
    CloseTime?: string;
    PerpetualAccess?: ScimReference[];
    WeekendAccess?: ScimReference[];
    WorkdayAccess?: ScimReference[];
    Rc?: ScimReference;
    School?: ScimReference;
    ObjectId?: string;
}

export interface FullClass {
    Id: string;
    DisplayName: string;
    EntityType: string | Reference; // Can be string or reference object
    Attendance?: boolean;
    Course?: string;
    CoursePeriods?: string[];
    EndDate?: string;
    Gpa?: boolean;
    GradeLevels?: string[];
    Grades?: boolean;
    Guardians?: ScimReference[];
    Labels?: string[];
    MarkingPeriods?: string[];
    Period?: string;
    Rc?: ScimReference;
    Room?: string;
    School?: ScimReference;
    SchoolName?: string;
    Sections?: string[];
    StartDate?: string;
    StudentCount?: number;
    Students?: ScimReference[];
    Subject?: string;
    Subjects?: string[];
    Teacher?: ScimReference;
    Teachers?: ScimReference[];
    Vacant?: boolean;
    ObjectId?: string;
}

export interface FullResponsibilityCenter {
    Id: string;
    DisplayName: string;
    EntityType: string | Reference; // Can be string or reference object
    Address?: string;
    Building?: ScimReference;
    City?: string;
    State?: string;
    Zip?: string;
    PhoneNumber?: string;
    Fax?: string;
    School?: ScimReference;
    Type?: string;
    ObjectId?: string;
}

export interface Class extends BaseLocality {
    // Class-specific fields if needed
}

export interface ResponsibilityCenter extends BaseLocality {
    // ResponsibilityCenter-specific fields if needed
}

export interface LocalityResponse<T> {
    localities: T[];
    /** Same as localities; for table/loader compatibility (ListResponse shape) */
    items?: T[];
    /** Same as localities; for table/loader compatibility */
    data?: T[];
    totalResults: number;
    currentPage: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    itemsPerPage: number;
    filter?: string;
    /** List-level $ and other response props (e.g. $timeTaken, $debug) */
    [key: string]: any;
}

// Individual SCIM attribute lists for each locality type
// Each locality type controls its own table attributes
export const BUILDING_SCIM_ATTRIBUTES = [
    'DisplayName',  // Maps to 'Name' column
    'EntityType',   // Maps to 'Type' column (for table display)
    'Id',          // Maps to 'Id' column
    'Address'      // Maps to 'Address' column
] as const;

export const SCHOOL_SCIM_ATTRIBUTES = [
    'DisplayName',  // Maps to 'Name' column
    'Type',         // Maps to 'Type' column (preferred)
    'EntityType',   // Maps to 'Type' column (fallback)
    'Id',           // Maps to 'Id' column
    'Address'       // Maps to 'Address' column
] as const;

export const CLASS_SCIM_ATTRIBUTES = [
    'DisplayName',  // Maps to 'Name' column
    'Course',       // Maps to 'Course' column (instead of Type)
    'Id',           // Maps to 'Id' column
    'Address'       // Maps to 'Address' column
] as const;

export const RESPONSIBILITY_CENTER_SCIM_ATTRIBUTES = [
    'DisplayName',  // Maps to 'Name' column
    'Type',         // Maps to 'Type' column (preferred)
    'EntityType',   // Maps to 'Type' column (fallback)
    'Id',           // Maps to 'Id' column
    'Address'       // Maps to 'Address' column
] as const;

// Mapping functions for each locality type
export function mapScimToBuilding(scimData: any): Building {
    return {
        DisplayName: scimData.DisplayName || '',
        Type: scimData.EntityType || '',
        Id: scimData.Id || '',
        Address: scimData.Address,
        Actions: [], // Placeholder for future
        rawData: scimData // Store the complete raw data for debug views
    };
}

export function mapScimToSchool(scimData: any): School {
    return {
        DisplayName: scimData.DisplayName || '',
        Type: scimData.Type || scimData.EntityType || '',
        Id: scimData.Id || '',
        Address: scimData.Address,
        Actions: [], // Placeholder for future
        rawData: scimData // Store the complete raw data for debug views
    };
}

export function mapScimToClass(scimData: any): Class {
    return {
        DisplayName: scimData.DisplayName || '',
        Type: scimData.Course || '',
        Id: scimData.Id || '',
        Address: scimData.Address,
        Actions: [], // Placeholder for future
        rawData: scimData // Store the complete raw data for debug views
    };
}

export function mapScimToResponsibilityCenter(scimData: any): ResponsibilityCenter {
    return {
        DisplayName: scimData.DisplayName || '',
        Type: scimData.Type || scimData.EntityType || '',
        Id: scimData.Id || '',
        Address: scimData.Address,
        Actions: [], // Placeholder for future
        rawData: scimData // Store the complete raw data for debug views
    };
} 